import { Theme } from '@mui/material';
/**
 * Shared style objects for stepper components
 * Provides consistent styling patterns across all stepper screens
 */

export const stepperStyles = {
  // Container styles
  pageContainer: {
    maxWidth: 800,
    mx: 'auto',
  },

  centeredContent: {
    textAlign: 'center' as const,
    mb: 2,
  },

  // Typography styles
  pageTitle: (theme: Theme) => ({
    fontWeight: 600,
    color: theme.palette.text.primary,
    mb: 2,
  }),

  pageDescription: (theme: Theme) => ({
    color: theme.palette.text.secondary,
    mb: 4,
    mx: 'auto',
    lineHeight: 1.6,
  }),

  sectionTitle: (theme: Theme) => ({
    fontWeight: 600,
    color: theme.palette.text.primary,
    mb: 3,
  }),

  instructionText: (theme: Theme) => ({
    color: theme.palette.text.primary,
    lineHeight: 1.5,
  }),

  // Paper/Card styles
  paperCard: (theme: Theme, elevation = 1) => ({
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    elevation,
  }),

  elevatedCard: (theme: Theme) => ({
    p: 3,
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    mb: 2,
  }),

  statusCard: (theme: Theme, isSuccess: boolean) => ({
    p: 2.5,
    mb: 3,
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: isSuccess
      ? theme.palette.success.light + '20'
      : theme.palette.warning.light + '20',
    elevation: 2,
  }),

  // Layout styles
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  flexColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
  },

  flexColumnGap: (gap: number) => ({
    display: 'flex',
    flexDirection: 'column' as const,
    gap,
  }),

  responsiveLayout: {
    display: 'flex',
    flexDirection: { xs: 'column', lg: 'row' } as const,
    gap: 3,
    alignItems: 'stretch',
  },

  // Form styles
  formContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 3,
  },

  inputGroup: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' } as const,
    gap: 2,
  },

  // Instruction/Step styles
  instructionStep: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 2,
  },

  numberedBadge: (backgroundColor: string) => ({
    minWidth: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    mt: 0.25,
  }),

  statusIndicator: (backgroundColor: string) => ({
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor,
  }),

  // Map/Content specific styles
  mapContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '600px',
    width: '100%',
  },

  sidebarPanel: {
    flex: { xs: '1 1 auto', lg: '0 0 300px' },
    order: { xs: 1, lg: 1 },
  },

  mainContentPanel: {
    flex: 1,
    order: { xs: 2, lg: 2 },
  },

  // Error/Message styles
  errorContainer: (theme: Theme) => ({
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `2px dashed ${theme.palette.error.main}`,
    borderRadius: 2,
    bgcolor: theme.palette.error.light + '10',
    m: 2,
  }),

  // Utility styles
  sticky: (top = 20) => ({
    position: { xs: 'static', lg: 'sticky' } as const,
    top: { lg: top },
  }),

  autoHeight: {
    height: { xs: 'auto', lg: '600px' },
  },

  fullHeight: {
    height: '600px',
  },

  // Button area styles
  buttonContainer: {
    display: 'flex',
    gap: 2,
    justifyContent: 'flex-start',
    alignItems: 'center',
  },

  buttonWithIcon: {
    minWidth: '120px',
    borderRadius: 2,
    textTransform: 'none' as const,
    fontWeight: 600,
  },

  secondaryButton: {
    minWidth: '100px',
    borderRadius: 2,
    textTransform: 'none' as const,
    fontWeight: 500,
  },

  addButton: {
    borderRadius: 2,
    textTransform: 'none' as const,
    fontWeight: 500,
  },

  elevatedButton: (theme: Theme) => ({
    minWidth: '120px',
    borderRadius: 2,
    textTransform: 'none' as const,
    fontWeight: 600,
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 2px 8px rgba(0,0,0,0.5)'
        : '0 2px 8px rgba(0,0,0,0.15)',
    '&:hover': {
      boxShadow:
        theme.palette.mode === 'dark'
          ? '0 4px 12px rgba(0,0,0,0.6)'
          : '0 4px 12px rgba(0,0,0,0.2)',
      transform: 'translateY(-1px)',
    },
    transition: 'all 0.2s ease-in-out',
  }),

  // Footer styles
  footerContainer: {
    flexShrink: 0,
    p: 1.5,
    borderTop: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    borderRadius: 0,
  },

  // Form input styles
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
    },
  },

  // Event configuration styles
  eventContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 1,
    marginLeft: 1,
  },

  eventConfigSection: {
    mb: 2,
  },

  // Information display styles
  infoNote: {
    fontSize: '0.875rem',
  },

  // Time configuration styles
  timeConfigContainer: {
    display: 'flex',
    flexDirection: 'row' as const,
    flexWrap: 'wrap',
    gap: 2,
    alignItems: 'flex-start',
    mb: 2,
  },

  checkboxGroup: {
    display: 'flex',
    gap: 1,
    alignItems: 'center',
    flexWrap: 'wrap',
    m: 0,
  },

  checkboxLabel: {
    mr: 0.5,
    mb: 0.5,
  },

  // Radio group styles
  radioGroup: {
    justifyContent: 'flex-start',
  },

  // Caption text styles
  captionText: (theme: Theme) => ({
    textAlign: 'left' as const,
    color: theme.palette.text.secondary,
  }),

  // Form label styles
  formLabel: (theme: Theme) => ({
    textAlign: 'left' as const,
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  }),

  // Event type list styles
  eventTypeList: {
    display: 'flex',
    flexDirection: { xs: 'column', lg: 'row' } as const,
    gap: 3,
  },

  // Compact time display
  compactTime: (theme: Theme) => ({
    fontSize: '0.95rem',
    color: theme.palette.text.secondary,
    mb: 2,
  }),

  // Large container for special screens
  largeContainer: {
    mx: 'auto',
    p: 1,
    maxWidth: 1200,
  },

  // Stepper header styles
  stepperHeaderPaper: {
    flexShrink: 0,
    borderBottom: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    borderRadius: 0,
  },

  stepperNavigation: (theme: Theme) => ({
    p: 2,
    minHeight: '60px',
    '& .MuiStepLabel-root': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      minWidth: 0,
    },
    '& .MuiStepLabel-iconContainer': {
      mr: 0,
      flexShrink: 0,
    },
    '& .MuiStepLabel-labelContainer': {
      flex: 1,
      display: 'flex',
      justifyContent: 'flex-start',
      minWidth: 0,
      pr: 1,
    },
    '& .MuiStepIcon-root': {
      fontSize: '1.2rem',
      '&.Mui-active': {
        color: 'primary.main',
      },
      '&.Mui-completed': {
        color: 'success.main',
      },
    },
  }),

  // Main stepper layout styles
  stepperContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    bgcolor: 'background.default',
  },

  stepperContentArea: {
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'auto',
    display: 'flex',
    justifyContent: 'center',
    p: 2,
  },

  stepperContentInner: {
    width: '100%',
    maxWidth: '900px',
    mx: 'auto',
  },

  stepperContentPaper: (theme: Theme) => ({
    padding: 1,
    pt: 2,
    margin: '0 auto',
    borderRadius: 2,
    bgcolor: 'background.paper',
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 2px 8px rgba(0,0,0,0.3)'
        : '0 2px 8px rgba(0,0,0,0.1)',
  }),

  // Error and alert styles
  errorAlert: (theme: Theme) => ({
    width: '100%',
    borderRadius: 2,
    backgroundColor: `${theme.palette.error.main}08`,
    border: `1px solid ${theme.palette.error.light}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center' as const,
    '& .MuiAlert-message': {
      width: '100%',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  }),

  errorTitle: (theme: Theme) => ({
    fontWeight: 600,
    color: theme.palette.error.dark,
    mb: 1,
  }),

  errorMessage: (theme: Theme) => ({
    color: theme.palette.error.dark,
    mb: 2,
    lineHeight: 1.5,
  }),

  errorCode: (theme: Theme) => ({
    color: theme.palette.error.dark,
    mb: 2,
    fontWeight: 600,
    fontFamily: 'monospace',
    backgroundColor: theme.palette.error.main + '20',
    p: 1,
    borderRadius: 1,
    maxWidth: '100%',
    wordBreak: 'break-word' as const,
  }),

  // Action and content containers
  centeredActionContainer: {
    textAlign: 'center' as const,
    mb: 3,
    mt: 3,
  },

  fullWidthCenteredContent: {
    width: '100%',
    textAlign: 'center' as const,
  },

  // Note/helper text styles
  helperNote: (theme: Theme) => ({
    fontStyle: 'italic !important',
    color: theme.palette.text.secondary,
  }),

  // Summary card styles
  summaryCard: (theme: Theme) => ({
    p: 2,
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  }),

  // Profile info styles
  profileInfoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  summaryLabel: (theme: Theme) => ({
    fontWeight: 600,
    color: theme.palette.text.primary,
  }),

  summaryValue: (theme: Theme) => ({
    fontWeight: 600,
    color: theme.palette.text.secondary,
  }),

  // Enhanced summary section styles
  summarySection: {
    mb: 4,
  },

  summaryHeader: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    mb: 3,
    pb: 1,
    borderBottom: `2px solid ${theme.palette.primary.main}`,
  }),

  profileCard: (theme: Theme, isEnabled: boolean) => ({
    p: 3,
    borderRadius: 3,
    border: `1px solid ${
      isEnabled ? theme.palette.success.main : theme.palette.warning.main
    }`,
    backgroundColor: isEnabled
      ? `${theme.palette.success.main}10`
      : `${theme.palette.warning.main}10`,
    position: 'relative',
    overflow: 'hidden',
    mb: 2,
  }),

  profileHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 3,
  },

  statusBadge: (theme: Theme, isEnabled: boolean) => ({
    px: 2,
    py: 0.75,
    borderRadius: 2,
    backgroundColor: isEnabled
      ? theme.palette.success.main
      : theme.palette.warning.main,
    color: 'white',
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    boxShadow: theme.shadows[2],
  }),

  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
    gap: 3,
  },

  metricCard: (theme: Theme) => ({
    textAlign: 'center',
    p: 2,
    borderRadius: 2,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[1],
  }),

  metricValue: (theme: Theme) => ({
    fontSize: '2rem',
    fontWeight: 800,
    color: theme.palette.primary.main,
    lineHeight: 1,
    mb: 0.5,
  }),

  metricLabel: (theme: Theme) => ({
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  }),

  userInfoGrid: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
    gap: 3,
  },

  infoItem: (theme: Theme) => ({
    textAlign: 'center',
    p: 2,
    borderRadius: 2,
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    minWidth: 150,
    flex: 1,
  }),

  infoLabel: (theme: Theme) => ({
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    mb: 1,
  }),

  infoValue: (theme: Theme) => ({
    fontSize: '1.1rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  }),

  // Debug panel styles
  debugPanel: (theme: Theme) => ({
    p: 3,
    borderRadius: 2,
    border: `2px solid ${theme.palette.error.main}`,
    backgroundColor: `${theme.palette.error.main}08`,
    fontFamily: 'monospace',
  }),

  debugTitle: (theme: Theme) => ({
    mb: 2,
    fontWeight: 600,
    color: theme.palette.error.main,
  }),

  debugContent: (theme: Theme) => ({
    maxHeight: 400,
    overflow: 'auto',
    backgroundColor: theme.palette.background.paper,
    p: 2,
    borderRadius: 1,
    border: `1px solid ${theme.palette.divider}`,
  }),

  debugLabel: (theme: Theme) => ({
    fontWeight: 600,
    color: theme.palette.error.dark,
    mb: 2,
  }),

  debugJson: (theme: Theme) => ({
    margin: 0,
    fontSize: '0.75rem',
    lineHeight: 1.4,
    color: theme.palette.text.primary,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
  }),

  // Enhanced JSON display styles
  jsonContainer: (theme: Theme) => ({
    maxHeight: 500,
    overflow: 'auto',
    backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f8f9fa',
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    position: 'relative',
  }),

  jsonHeader: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    p: 2,
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.mode === 'dark' ? '#252526' : '#ffffff',
    fontFamily: 'monospace',
  }),

  jsonContent: (theme: Theme) => ({
    p: 3,
    fontFamily:
      '"Fira Code", "JetBrains Mono", "Consolas", "Monaco", monospace',
    fontSize: '0.8rem',
    lineHeight: 1.6,
    color: theme.palette.text.primary,
    '& .json-key': {
      color: theme.palette.mode === 'dark' ? '#9CDCFE' : '#0451A5',
      fontWeight: 600,
    },
    '& .json-string': {
      color: theme.palette.mode === 'dark' ? '#CE9178' : '#A31515',
    },
    '& .json-number': {
      color: theme.palette.mode === 'dark' ? '#B5CEA8' : '#098658',
    },
    '& .json-boolean': {
      color: theme.palette.mode === 'dark' ? '#569CD6' : '#0000FF',
      fontWeight: 600,
    },
    '& .json-null': {
      color: theme.palette.mode === 'dark' ? '#569CD6' : '#0000FF',
      fontStyle: 'italic',
    },
    '& .json-bracket': {
      color: theme.palette.mode === 'dark' ? '#D4D4D4' : '#000000',
      fontWeight: 700,
    },
  }),

  copyButton: (theme: Theme) => ({
    fontSize: '0.75rem',
    minWidth: 'auto',
    px: 1.5,
    py: 0.5,
    backgroundColor: theme.palette.mode === 'dark' ? '#404040' : '#e9ecef',
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? '#505050' : '#dee2e6',
    },
  }),
};

/**
 * Theme-aware color utilities
 */
export const stepperColors = {
  primary: (theme: Theme) => theme.palette.primary.main,
  secondary: (theme: Theme) => theme.palette.secondary.main,
  error: (theme: Theme) => theme.palette.error.main,
  warning: (theme: Theme) => theme.palette.warning.main,
  success: (theme: Theme) => theme.palette.success.main,
  textPrimary: (theme: Theme) => theme.palette.text.primary,
  textSecondary: (theme: Theme) => theme.palette.text.secondary,
  divider: (theme: Theme) => theme.palette.divider,
};

/**
 * Common spacing values
 */
export const stepperSpacing = {
  xs: 1,
  sm: 2,
  md: 3,
  lg: 4,
  xl: 5,
};
